import type { CardColor, SkillId } from "../schemas"
import { enUSMessages } from "./locales/en-US"
import type { MessageId, MessageValues } from "./messages"

type TranslateFn = (id: MessageId, values?: MessageValues) => string

const factionMessageIds: Record<CardColor, MessageId> = {
  blue: "faction.blue",
  colorless: "faction.colorless",
  green: "faction.green",
  purple: "faction.purple",
  red: "faction.red",
  yellow: "faction.yellow",
}

const skillNameMessageIds: Record<SkillId, MessageId> = {
  armor: "skill.armor.name",
  battery: "skill.battery.name",
  broker: "skill.broker.name",
  breakthrough: "skill.breakthrough.name",
  charge: "skill.charge.name",
  coordinated_strike: "skill.coordinated_strike.name",
  deploy: "skill.deploy.name",
  focus: "skill.focus.name",
  generator: "skill.generator.name",
  guard: "skill.guard.name",
  jam: "skill.jam.name",
  ko: "skill.ko.name",
  rally: "skill.rally.name",
  ranged: "skill.ranged.name",
  recover: "skill.recover.name",
  recycle: "skill.recycle.name",
  repair: "skill.repair.name",
  salvage: "skill.salvage.name",
  scout: "skill.scout.name",
  scrap: "skill.scrap.name",
  shield: "skill.shield.name",
  shift: "skill.shift.name",
  sneak: "skill.sneak.name",
  spark: "skill.spark.name",
  swift: "skill.swift.name",
}

const skillDescriptionMessageIds: Record<SkillId, MessageId> = {
  armor: "skill.armor.description",
  battery: "skill.battery.description",
  broker: "skill.broker.description",
  breakthrough: "skill.breakthrough.description",
  charge: "skill.charge.description",
  coordinated_strike: "skill.coordinated_strike.description",
  deploy: "skill.deploy.description",
  focus: "skill.focus.description",
  generator: "skill.generator.description",
  guard: "skill.guard.description",
  jam: "skill.jam.description",
  ko: "skill.ko.description",
  rally: "skill.rally.description",
  ranged: "skill.ranged.description",
  recover: "skill.recover.description",
  recycle: "skill.recycle.description",
  repair: "skill.repair.description",
  salvage: "skill.salvage.description",
  scout: "skill.scout.description",
  scrap: "skill.scrap.description",
  shield: "skill.shield.description",
  shift: "skill.shift.description",
  sneak: "skill.sneak.description",
  spark: "skill.spark.description",
  swift: "skill.swift.description",
}

export function getFactionName(t: TranslateFn, color: CardColor) {
  return t(factionMessageIds[color])
}

export function getFactionSearchValues(t: TranslateFn, color: CardColor) {
  return dedupeValues([
    color,
    enUSMessages[factionMessageIds[color]],
    getFactionName(t, color),
  ])
}

export function getAllFactionSearchValues(t: TranslateFn) {
  return dedupeValues(
    (Object.keys(factionMessageIds) as CardColor[]).flatMap((color) =>
      getFactionSearchValues(t, color),
    ),
  )
}

export function getSkillDescription(t: TranslateFn, skillId: SkillId) {
  return t(skillDescriptionMessageIds[skillId])
}

export function getSkillName(t: TranslateFn, skillId: SkillId) {
  return t(skillNameMessageIds[skillId])
}

export function getSkillSearchValues(t: TranslateFn, skillId: SkillId) {
  return dedupeValues([
    skillId,
    enUSMessages[skillNameMessageIds[skillId]],
    getSkillName(t, skillId),
  ])
}

export function getAllSkillSearchValues(t: TranslateFn) {
  return dedupeValues(
    (Object.keys(skillNameMessageIds) as SkillId[]).flatMap((skillId) =>
      getSkillSearchValues(t, skillId),
    ),
  )
}

function dedupeValues(values: readonly string[]) {
  return [...new Set(values)]
}
